import React, { useState } from 'react'
import { View, TouchableOpacity, StyleSheet } from 'react-native'
import Colors from '../../../constants/Colors'
import RewardsList from '../../../components/Rewards/RewardsList'
import { auth } from '../../../firebase/config'
import { MAX_CHECKOUT_WIDTH, useGlobalStyles } from '../../../constants/GlobalStyle'
import { responsiveWidth } from 'react-native-responsive-dimensions'
import { Text } from '../../../components/Themed'
import { PickupPointModal } from '../../../components/PickupPoint'
import { useAppSelector } from '../../../state/hooks'
import DeliveryDetailsModal from '../../../components/Delivery/DeliveryDetails/DeliveryDetailsModal'

/**
 * Boxes that appear on checkout page (non-kiosk only)
 */
export const RewardsBox = ({ navigation }) => {
	const { userData } = useAppSelector(state => state.user)
	if (auth.currentUser !== null && userData !== null) {
		return (
			<View style={styles.container}>
				<Text
					style={[
						styles.label,
						{
							marginHorizontal: '5%',
							alignSelf: 'center',
							width: responsiveWidth(90),
							maxWidth: MAX_CHECKOUT_WIDTH,
						},
					]}
				>
					{'REWARDS'}
				</Text>
				<RewardsList
					showAll={false}
					onlyRedeemable={true}
					navigation={navigation}
					isCheckout={true}
				/>
			</View>
		)
	}
}

export const ChangeLocationBox = ({ toggleLocationModal, currentAddress }) => {
	const { deliveryDetailInput } = useGlobalStyles()
	return (
		<View style={styles.container}>
			<View style={styles.inputGroup}>
				<Text style={styles.label}>{'PICKUP LOCATION'}</Text>
				<View style={styles.inputRow}>
					<View style={[deliveryDetailInput]}>
						<>
							<Text>{currentAddress}</Text>
						</>
					</View>

					<TouchableOpacity onPress={toggleLocationModal} style={styles.button}>
						<Text style={styles.buttonText}>Change</Text>
					</TouchableOpacity>
				</View>
			</View>
		</View>
	)
}

export const PickupTimeBox = ({ toggleTimeModal, pickupText, isOpen, displayAsOption = false }) => {
	const { cartPrep, isDelivery, deliveryEstimate, cart } = useAppSelector(state => state.persisted)
	const { deliveryDetailInput } = useGlobalStyles()
	const deliveryTime =
		deliveryEstimate && deliveryEstimate.estimate ? deliveryEstimate.estimate.timeToDeliver : 60

	const hasItems = cart.length > 0

	const getPickupTimeText = () => {
		if (cartPrep.isScheduled) {
			return pickupText
		} else if (isOpen && isDelivery && deliveryTime >= 0) {
			return `ASAP${hasItems ? ` (${deliveryTime} minutes)` : ''}`
		} else if (isOpen && isDelivery) {
			return 'Next available time'
		} else if (isOpen) {
			return `ASAP${hasItems ? ` (${cartPrep.longestPrepTime} minutes)` : ''}`
		} else {
			return 'Closed'
		}
	}

	return (
		<View style={styles.container}>
			<View style={displayAsOption ? {} : styles.inputGroup}>
				{!displayAsOption && (
					<Text style={styles.label}>{isDelivery ? 'ESTIMATED DELIVERY TIME' : 'PICKUP TIME'}</Text>
				)}
				<View style={styles.inputRow}>
					<View style={[displayAsOption ? {} : deliveryDetailInput]}>
						<Text style={displayAsOption ? { fontWeight: '700' } : {}}>{getPickupTimeText()}</Text>
					</View>

					{hasItems && (
						<TouchableOpacity
							onPress={toggleTimeModal}
							style={[styles.button, { marginRight: displayAsOption ? 10 : 0 }]}
						>
							<Text style={styles.buttonText}>
								{isOpen || cartPrep.isScheduled ? 'Change' : 'Schedule Order'}
							</Text>
						</TouchableOpacity>
					)}
				</View>
			</View>
		</View>
	)
}

export const PickupPointDetailsBox = ({ showModal, setShowModal, numOfRestaurants }) => {
	const { selectedPickupPoint, isDelivery } = useAppSelector(state => state.persisted)
	const { pickupPointsEnabled } = useAppSelector(state => state.settings)
	const { deliveryDetailInput } = useGlobalStyles()

	if (
		pickupPointsEnabled &&
		selectedPickupPoint !== null &&
		selectedPickupPoint.name &&
		!isDelivery
	) {
		let addressSpace = ''
		const address = selectedPickupPoint.address
		let formattedAddressLine = ''
		let formattedCityLine = ''
		if (address) {
			if (address.addressLine2 && address.addressLine2 !== '') {
				addressSpace = ' '
			}
			formattedAddressLine = `${address.addressLine1}${
				address.addressLine2 ? ' ' + address.addressLine2 : ''
			}`
			formattedCityLine = `${address.city}, ${address.provinceState} ${address.postCode}`
		}
		return (
			<>
				<View style={styles.container}>
					<View style={styles.inputGroup}>
						<Text style={styles.label}>{'PICKUP DETAILS'}</Text>
						<View style={styles.inputRow}>
							<View style={[{ width: '80%' }, deliveryDetailInput]}>
								<Text style={{ width: '80%' }}>
									{selectedPickupPoint.name}
									{selectedPickupPoint.id === 'store_pickup' && numOfRestaurants > 1 && 's'}
									{selectedPickupPoint.address
										? ': ' + formattedAddressLine + ', ' + formattedCityLine
										: ''}
								</Text>
							</View>

							<TouchableOpacity
								onPress={() => {
									setShowModal(!showModal)
								}}
								style={styles.button}
							>
								<Text style={styles.buttonText}>{'Change'}</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
				<PickupPointModal visible={showModal} setVisible={setShowModal} onChange={true} />
			</>
		)
	}
	return <></>
}

export const DeliveryDetailsBox = ({ errorMessages }) => {
	const { isDelivery } = useAppSelector(state => state.persisted)
	const { userLocation } = useAppSelector(state => state.user)
	const { deliveryDetailInput } = useGlobalStyles()
	const [open, setOpen] = useState(false)

	if (!isDelivery) return <></>

	return (
		<>
			<View style={styles.container}>
				<View style={styles.inputGroup}>
					<Text style={styles.label}>{'DELIVERY DETAILS'}</Text>
					<View style={styles.inputRow}>
						<View style={[{ width: '80%' }, deliveryDetailInput]}>
							<Text style={{ width: '80%' }}>
								{userLocation && userLocation.longAddress ? (
									userLocation.longAddress
								) : (
									<Text style={{ color: Colors.greyscale[7] }}>No address selected</Text>
								)}
							</Text>
						</View>

						<TouchableOpacity
							onPress={() => {
								setOpen(true)
							}}
							style={styles.button}
						>
							<Text style={styles.buttonText}>{'Change'}</Text>
						</TouchableOpacity>
					</View>
					<View style={{ marginTop: 8 }}>{errorMessages}</View>
				</View>
			</View>
			{open && <DeliveryDetailsModal closeModal={() => setOpen(false)} />}
		</>
	)
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		justifyContent: 'center',
	},
	inputGroup: {
		marginBottom: 20,
	},
	label: {
		fontWeight: 'bold',
		marginBottom: 10,
		fontSize: 16,
	},
	inputRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	input: {
		flexGrow: 1,
		height: 55,
		justifyContent: 'center',
		padding: 15,
		borderWidth: 1,
		borderColor: Colors.greyscale[5],
		borderRadius: 8,
		backgroundColor: '#fff',
	},
	button: {
		position: 'absolute',
		right: 10,
		padding: 15,
	},
	buttonText: {
		color: 'black',
		fontWeight: '500',
	},
})
